import {getRunningTimeDuration} from '@domain/running-times'
import {RunningTime, RunningTimeType, StoppageKind} from '@hconnect/common/types'
import {mergeSxs} from '@hconnect/uikit'
import {Box, darken, lighten, Stack, Tooltip, Typography, Theme} from '@mui/material'
import {RUNNING_TIME_COLOR} from '@widgets/common-running-times'
import {isEmpty} from 'lodash'
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {RUNNING_TIMES_WIDGET_NAMESPACE} from '../../consts'
import {BarForChartProps} from '../../types'

const smallBarSx = {
  height: 12
}

const getResolvedColor = (theme: Theme, colorKey: string): string => {
  const [mainKey, shade] = colorKey.split('.')
  return theme.palette[mainKey]?.[shade] ?? colorKey
}

export const BarForChart: React.FC<BarForChartProps> = ({
  percentage,
  timezone,
  runningTime,
  stoppageIndex,
  handleStoppageClick,
  handleRunningClick,
  sx,
  aboveBarComponent,
  children,
  tooltip,
  isEditMode
}) => {
  const {i18n, t} = useTranslation(RUNNING_TIMES_WIDGET_NAMESPACE)
  const language = i18n.language

  const getTimeTooltipContent = useCallback(
    (runningTime: RunningTime, stoppageIndex = 0) => {
      const begin = !isEmpty(runningTime.stoppages)
        ? runningTime.stoppages?.[stoppageIndex].start
        : runningTime.beginTechnical
      const end = !isEmpty(runningTime.stoppages)
        ? runningTime.stoppages?.[stoppageIndex].end
        : runningTime.endTechnical

      return (
        <Stack spacing={2} data-test-id={`tooltip-${runningTime.runningTimeType}`}>
          {tooltip?.header}
          {tooltip?.body || (
            <Typography>
              {getRunningTimeDuration({
                beginUtc: begin?.toISOString(),
                endUtc: end?.toISOString(),
                timezone: timezone,
                runningTimeType: runningTime.runningTimeType,
                language,
                translationFn: t
              })}
            </Typography>
          )}
          {tooltip?.footer}
        </Stack>
      )
    },
    [tooltip, language, t, timezone]
  )

  if (!runningTime?.runningTimeType) {
    return (
      <Box sx={{flexBasis: percentage}}>
        {aboveBarComponent}
        <Box
          sx={mergeSxs(
            {
              flexBasis: percentage
            },
            (theme: Theme) => getStyle(theme, undefined),
            smallBarSx,
            sx
          )}
        />
      </Box>
    )
  }

  return (
    <>
      {runningTime && (
        <Tooltip arrow title={getTimeTooltipContent(runningTime, stoppageIndex)}>
          <Box
            onClick={
              runningTime.runningTimeType === RunningTimeType.STOPPED
                ? handleStoppageClick
                : handleRunningClick
            }
            sx={mergeSxs(
              {
                flexBasis: percentage,
                zIndex: 1,
                display: 'flex',
                justifyContent: 'center'
              },
              (theme: Theme) =>
                getStyle(
                  theme,
                  runningTime,
                  !!handleStoppageClick,
                  !!handleRunningClick,
                  stoppageIndex,
                  isEditMode
                ),
              smallBarSx,
              sx
            )}
            data-test-id={`bar-for-chart-${runningTime.runningTimeType}`}
          >
            {children}
          </Box>
        </Tooltip>
      )}
    </>
  )
}

function getDefinedStoppageStyle(
  theme: Theme,
  runningTime: RunningTime,
  stoppedClickable?: boolean,
  stoppageIndex: number = 0,
  isEditMode?: boolean
) {
  return runningTime.stoppages?.[stoppageIndex]?.stoppageKind === StoppageKind.PlannedStoppage
    ? {
        backgroundColor: isEditMode
          ? lighten(getResolvedColor(theme, RUNNING_TIME_COLOR.planned), 0.85)
          : RUNNING_TIME_COLOR.planned,
        cursor: 'pointer',
        ...(stoppageIndex &&
          !isEditMode && {
            borderLeft: `1px solid ${darken(theme.palette.grey['700'], 0.15)}`
          })
      }
    : {
        backgroundColor: isEditMode
          ? lighten(getResolvedColor(theme, RUNNING_TIME_COLOR.stoppedDefined), 0.85)
          : RUNNING_TIME_COLOR.stoppedDefined,
        cursor: stoppedClickable ? 'pointer' : 'default',
        ...(stoppageIndex &&
          !isEditMode && {
            borderLeft: `1px solid ${darken(theme.palette.error.main, 0.15)}`
          }),
        ...(!isEditMode && {
          '&:hover': {
            backgroundColor: theme.palette.error.main
          },
          '&:active': {
            backgroundColor: theme.palette.error.dark
          }
        })
      }
}

function getStyle(
  theme: Theme,
  runningTime: RunningTime | undefined,
  stoppedClickable?: boolean,
  runningClickable?: boolean,
  stoppageIndex: number = 0,
  isEditMode?: boolean
) {
  switch (runningTime?.runningTimeType) {
    case RunningTimeType.RUNNING:
      return {
        backgroundColor: RUNNING_TIME_COLOR.running,
        cursor: runningClickable ? 'pointer' : 'default'
      }
    case RunningTimeType.STOPPED:
      if (!runningTime.stoppages?.[stoppageIndex]?.cockpitStoppageId) {
        return {
          backgroundColor: RUNNING_TIME_COLOR.stoppedUndefined,
          cursor: stoppedClickable ? 'pointer' : 'default'
        }
      }
      return getDefinedStoppageStyle(
        theme,
        runningTime,
        stoppedClickable,
        stoppageIndex,
        isEditMode
      )
    case undefined:
    case null:
    default:
      return {backgroundColor: 'grey.200'}
  }
}
